import { useCallback, useEffect, useState } from "react";
import { Megaphone, Sparkle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Ads() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [ads, setAds] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ item_id: "", product: "", details: "", price: "",
    market: "US", engine: "gpt", picture_engine: "gemini", public: true });

  const load = useCallback(() => {
    api.get("/ads").then(({ data }) => setAds(data)).catch(oops);
  }, []);

  useEffect(() => {
    api.get("/ads/meta").then(({ data }) => setMeta(data)).catch(oops);
    api.get("/items").then(({ data }) => setItems(data.slice(0, 200))).catch(() => setItems([]));
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ads", {
        ...form, item_id: form.item_id || null,
        product: form.product.trim() || null,
        details: form.details.trim() || null,
        price: form.price ? Number(form.price) : null });
      toast.success(`Advert written — ${data.headline}`);
      setForm({ ...form, product: "", details: "", price: "", item_id: "" });
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const pick = async (id, angle) => {
    try { await api.post(`/ads/${id}/pick`, { angle }); toast.success(`Using the ${angle} version`); load(); }
    catch (e) { oops(e); }
  };

  const togglePublic = async (id, on) => {
    try { await api.post(`/ads/${id}/public?public=${on}`); load(); } catch (e) { oops(e); }
  };

  const remove = async (id) => {
    try { await api.delete(`/ads/${id}`); toast.success("Advert deleted"); load(); } catch (e) { oops(e); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ads/refresh");
      toast.success(`${data.refreshed} advert(s) redrawn`);
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!meta) return <p className="py-10 text-center text-sm text-slate-500">Loading advertising…</p>;

  return (
    <div className="space-y-5" data-testid="ads-page">
      <PageHeader overline="Reach" title="Advertising" testid="ads-header"
        description="Turn any tool into an advert — written for the market you are selling into, with its own picture, buy-it links and one-tap sharing." />

      <div className="flex flex-wrap items-center gap-2">
        <a href="/ads" target="_blank" rel="noreferrer" data-testid="ads-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public adverts board
        </a>
        <ShareBar url={`${BACKEND}/api/share/ads`} text="Tools and machines on offer"
          testid="ads-board-share" compact instagram />
        {user?.role === "admin" && (
          <Button variant="outline" data-testid="ads-refresh" onClick={refresh} disabled={busy}>
            <Sparkle size={15} className="mr-1.5" /> {busy ? "Working…" : "Redraw today's adverts"}
          </Button>
        )}
      </div>

      {meta.can_manage && (
        <div className="border border-border bg-white p-4" data-testid="ads-form">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
            <Megaphone size={15} className="text-[#002FA7]" /> New advert
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">From stock (optional)</Label>
              <select data-testid="ads-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })}>
                <option value="">Type it myself</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Product</Label>
              <Input data-testid="ads-product" value={form.product} placeholder="18V brushless impact driver"
                onChange={(e) => setForm({ ...form, product: e.target.value })} /></div>
            <div><Label className="text-xs">Price</Label>
              <Input data-testid="ads-price" type="number" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div>
              <Label className="text-xs">Market</Label>
              <select data-testid="ads-market" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })}>
                {meta.markets.map((m) => <option key={m.code} value={m.code}>{m.label} · {m.currency}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Copy written by</Label>
              <select data-testid="ads-engine" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.engine} onChange={(e) => setForm({ ...form, engine: e.target.value })}>
                {meta.engines.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Picture drawn by</Label>
              <select data-testid="ads-picture-engine" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.picture_engine} onChange={(e) => setForm({ ...form, picture_engine: e.target.value })}>
                {meta.picture_engines.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Anything to mention</Label>
              <Textarea data-testid="ads-details" rows={2} value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })} /></div>
          </div>
          <label className="tap-safe mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input data-testid="ads-public" type="checkbox" className="h-5 w-5 shrink-0"
              checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} />
            Public and shareable
          </label>
          <Button className="mt-3 w-full sm:w-auto" data-testid="ads-create" onClick={create}
            disabled={busy || (!form.item_id && form.product.trim().length < 3)}>
            {busy ? "Writing and drawing…" : "Write the advert"}
          </Button>
        </div>
      )}

      {ads.length === 0 ? (
        <EmptyState testid="ads-empty" title="No adverts yet"
          description="Pick a tool from stock or type a product, choose the market, and the advert writes itself with its own picture." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="ads-list">
          {ads.map((ad, i) => (
            <div key={ad.id} data-testid={`ad-${i}`} className="border border-border bg-white">
              {ad.image_url && <img src={`${BACKEND}${ad.image_url}`} alt={ad.product} className="h-52 w-full object-cover" />}
              <div className="p-4">
                <p className="overline text-[#B3241B]">{ad.market_label} · {ad.currency}</p>
                <p className="font-display mt-1 text-lg font-bold text-[#0F172A]">{ad.headline}</p>
                <p className="mt-2 text-sm text-slate-700">{ad.copy}</p>
                <p className="mt-2 text-xl font-bold text-[#0B5C2E]">{ad.symbol}{ad.price}</p>
                <div className="scroll-x mt-3 flex gap-1.5 overflow-x-auto">
                  {ad.variations.map((v) => (
                    <button key={v.angle} data-testid={`ad-${i}-angle-${v.angle}`} onClick={() => pick(ad.id, v.angle)}
                      className="tap-safe shrink-0 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
                      Use {v.angle}
                    </button>
                  ))}
                </div>
                <div className="scroll-x mt-3 flex gap-1.5 overflow-x-auto" data-testid={`ad-${i}-links`}>
                  {ad.links.map((l) => (
                    <a key={l.key} href={l.url} target="_blank" rel="noreferrer"
                      className="tap-safe shrink-0 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]">
                      {l.label}
                    </a>
                  ))}
                </div>
                <div className="mt-3">
                  <ShareBar url={`${BACKEND}/api/share/ad/${ad.token}`} text={ad.headline}
                    testid={`ad-${i}-share`} compact instagram />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3 text-xs text-slate-500">
                  <label className="tap-safe flex items-center gap-2">
                    <input data-testid={`ad-${i}-public`} type="checkbox" className="h-5 w-5 shrink-0"
                      checked={ad.public} onChange={(e) => togglePublic(ad.id, e.target.checked)} />
                    Public
                  </label>
                  <span>Written by {ad.engine_label}</span>
                  {user?.role === "admin" && (
                    <button data-testid={`ad-${i}-delete`} onClick={() => remove(ad.id)}
                      className="tap-safe ml-auto inline-flex items-center gap-1.5 text-slate-500 hover:text-[#B3241B]">
                      <Trash size={13} /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
